import { FormEvent, useState } from "react";
import {
  createSessionShortCodeAccessRequest,
  resolveSessionShortCode,
} from "../services/sessionShortCodeService";
import type {
  SessionShortCodeLobby,
  SessionShortCodeLobbyParticipant,
} from "../types/session";

function formatDate(value: Date | null): string {
  if (!value) {
    return "Date non communiquée";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(value);
}

function initialCode(): string {
  const params = new URLSearchParams(window.location.hash.split("?")[1] ?? "");
  return params.get("code") ?? "";
}

export function ParticipantLobby() {
  const [code, setCode] = useState(initialCode);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [lastName, setLastName] = useState("");
  const [lobby, setLobby] = useState<SessionShortCodeLobby | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [selectedParticipant, setSelectedParticipant] =
    useState<SessionShortCodeLobbyParticipant | null>(null);

  async function handleResolve(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsLoading(true);
    setMessage(null);
    setSelectedParticipant(null);

    try {
      const nextLobby = await resolveSessionShortCode(code);
      setLobby(nextLobby);
      setMessage(
        nextLobby
          ? null
          : "Cette clé est inconnue, inactive ou la session n'est pas encore ouverte.",
      );
    } catch (error) {
      console.error(error);
      setLobby(null);
      setMessage("Impossible de vérifier cette clé pour le moment.");
    } finally {
      setIsLoading(false);
    }
  }

  function selectParticipant(participant: SessionShortCodeLobbyParticipant) {
    setSelectedParticipant(participant);
    setFirstName(participant.firstName);
    setLastName(participant.lastName);
    setMessage(null);
  }

  function clearParticipantSelection() {
    setSelectedParticipant(null);
    setFirstName("");
    setLastName("");
    setEmail("");
    setMessage(null);
  }

  async function handleAccessRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!lobby) {
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      await createSessionShortCodeAccessRequest({
        draft: {
          email,
          firstName,
          lastName,
          participantSlotId: selectedParticipant?.id ?? null,
        },
        shortCode: lobby.id,
      });
      setMessage("Demande envoyée. Le formateur voit votre demande d'accès.");
      setEmail("");
    } catch (error) {
      console.error(error);
      setMessage("Impossible d'envoyer la demande d'accès.");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <main className="public-shell">
      <section className="auth-panel public-panel">
        <p className="eyebrow">Qorelia Formation</p>
        <h1>Accueil session</h1>
        <form className="inline-form" onSubmit={handleResolve}>
          <label className="field">
            <span>Clé courte</span>
            <input
              autoComplete="one-time-code"
              inputMode="text"
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              placeholder="ABCDE"
              required
              value={code}
            />
          </label>
          <button className="button button-primary" disabled={isLoading} type="submit">
            Rejoindre
          </button>
        </form>
        {message ? (
          <div className="alert alert-info" role="status">
            {message}
          </div>
        ) : null}
      </section>

      {lobby ? (
        <section className="public-content">
          <article className="panel panel-wide">
            <p className="eyebrow">Session ouverte</p>
            <h2>{lobby.title}</h2>
            <div className="capability-strip">
              <span>Début : {formatDate(lobby.publicStartAt)}</span>
              <span>Fin : {formatDate(lobby.publicEndAt)}</span>
              <span>Timezone : {lobby.timezone}</span>
            </div>
          </article>

          <article className="panel">
            <p className="eyebrow">Participants attendus</p>
            <h2>Votre nom</h2>
            {lobby.participants.length > 0 ? (
              <ul className="resource-list">
                {lobby.participants.map((participant) => (
                  <li key={participant.id}>
                    <button
                      className={
                        selectedParticipant?.id === participant.id
                          ? "resource-button resource-button-active"
                          : "resource-button"
                      }
                      onClick={() => selectParticipant(participant)}
                      type="button"
                    >
                      <strong>
                        {participant.firstName} {participant.lastName}
                      </strong>
                      <span>{participant.emailMasked ?? "Email à renseigner"}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>Aucun participant attendu n'est affiché.</p>
            )}
            <button
              className="button button-secondary"
              onClick={clearParticipantSelection}
              type="button"
            >
              Je ne suis pas dans la liste
            </button>
          </article>

          <article className="panel">
            <p className="eyebrow">Demande d'accès</p>
            <h2>Prévenir le formateur</h2>
            <form className="stack" onSubmit={handleAccessRequest}>
              <label className="field">
                <span>Prénom</span>
                <input
                  onChange={(event) => setFirstName(event.target.value)}
                  required
                  type="text"
                  value={firstName}
                />
              </label>
              <label className="field">
                <span>Nom</span>
                <input
                  onChange={(event) => setLastName(event.target.value)}
                  required
                  type="text"
                  value={lastName}
                />
              </label>
              <label className="field">
                <span>Email</span>
                <input
                  autoComplete="email"
                  inputMode="email"
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  type="email"
                  value={email}
                />
              </label>
              <button
                className="button button-primary"
                disabled={isLoading || !firstName.trim() || !lastName.trim()}
                type="submit"
              >
                Envoyer la demande
              </button>
            </form>
          </article>
        </section>
      ) : null}
    </main>
  );
}
